<template>
  <section
    ref="$block"
    :class="{
      'osk-home-blocks-single-banner': true,
      'osk-home-blocks-single-banner_isButton': isButtonInsideBanner,
      'osk-home-blocks-single-banner_isJournalMode': isJournalMode,
    }"
  >
    <div class="osk-wrapper">
      <component
        :is="component"
        class="osk-home-blocks-single-banner__card"
        :to="isLink ? item.link : undefined"
      >
        <img
          v-if="item.imgPath"
          :src="item.imgPath"
          alt="main-image"
          class="osk-home-blocks-single-banner__card-image"
        >
        <div
          :class="{
            'osk-home-blocks-single-banner__card-content': true,
            'osk-home-blocks-single-banner__card-content_noImage': !item.imgPath,
          }"
        >
          <h1 class="osk-home-blocks-single-banner__card-title">
            {{ item.title }}
          </h1>

          <p
            v-if="!isMounted && item.description"
            class="osk-home-blocks-single-banner__card-text osk-home-blocks-single-banner__card-text_ssr"
          >
            {{ item.description }}
          </p>

          <text-clamp
            v-else-if="item.description"
            class="osk-home-blocks-single-banner__card-text"
            :text="item.description"
            :max-lines="2"
          >
            <template #after="{ toggle, clamped, expanded }">
              <span
                class="osk-home-blocks-single-banner__card-text_toggle"
                @click="toggle"
              >
                <template v-if="clamped">Подробнее</template>

                <template v-else-if="expanded">Скрыть</template>
              </span>
            </template>
          </text-clamp>

          <span
            v-if="isJournalMode && item.additionalDescription"
            class="osk-home-blocks-single-banner__card-subText"
          >
            {{ item.additionalDescription }}
          </span>

          <div
            v-if="isMoreLinkExist"
            class="osk-home-blocks-single-banner__card-more-link"
            @click.prevent.stop="handleMoreLink"
          >
            {{ item.descriptionButtonText }}
          </div>

          <div
            v-if="isButtonExist || isMoreLinkExist"
            class="osk-home-blocks-single-banner__buttons"
          >
            <UikitButton
              v-if="isMoreLinkExist"
              class="osk-home-blocks-single-banner__card-more-btn"
              :size="!isMounted || isDesktop ? 's' : 'm'"
              outline
              @click.prevent.stop="handleMoreButton"
            >
              {{ item.descriptionButtonText }}
            </UikitButton>

            <UikitButton
              v-if="isButtonExist"
              class="osk-home-blocks-single-banner__button"
              :size="!isMounted || isDesktop ? 's' : 'm'"
              :outline="(isMounted && !(isDesktop || laptopM)) || isJournalMode"
              :color="isMounted && !(isDesktop || laptopM) && isButtonInsideBanner? 'white' : undefined"
              @click.prevent.stop="handleButtonClick"
            >
              {{ buttonTitle }}
            </UikitButton>
          </div>
        </div>
      </component>
    </div>

    <BusinessMoreDetailsDialog
      v-if="isMoreLinkExist"
      v-model="moreDetailsDialogData.open"
      :link="moreDetailsDialogData.link"
    />
  </section>
</template>

<script lang="ts" setup>
import TextClamp from 'vue3-text-clamp';

import { PrimaryContentDTO, BannerDTO } from '~/restAPI/data-contracts';
import { AnalyticsEvents } from '~/types/analytics';

const props = defineProps<{
  data: PrimaryContentDTO
  isMoreDetails?: boolean
}>();

const $block = ref<ComponentPublicInstance>();

useScrollEvent($block, event);

const { params: { homeBannerId } } = useRoute();
const { isConciergeDialogOpen } = useConcierge();
const { isDesktop, isMounted, laptopM } = useUiBreakpoints();
const { $addEvent } = useNuxtApp();

const moreDetailsDialogData = ref({
  open: false,
  link: '',
});

const item = computed<BannerDTO>(() => {
  const dataContent = (props.data.content ?? {}) as BannerDTO;

  return {
    ...dataContent,
    link: genSPALink(dataContent.link),
    descriptionButtonUrl: genSPALink(dataContent.descriptionButtonUrl),
  };
});

const isJournalMode = computed(() => item.value.isJournalMode);
const isButtonInsideBanner = computed(() => item.value.isButtonInsideBanner);
const isLink = computed(() => item.value.link && !item.value.link?.includes(homeBannerId as string));

const component = computed(() => {
  if (isLink.value && !props.isMoreDetails) return defineNuxtLink({});

  return 'div';
});

const buttonTitle = computed(() => {
  if (item.value.buttonLink?.includes('concierge_form') && !item.value.buttonTitle) return 'Создать заявку';
  if (item.value.buttonTitle) return item.value.buttonTitle;

  return '';
});

const isButtonExist = computed(() => !!(item.value.buttonLink && buttonTitle.value));
const isMoreLinkExist = computed(() => !!(
  item.value.descriptionButtonText
  && item.value.descriptionButtonUrl // ?.includes('homeBanner')
));
const analytics = computed(() => ({
  chapter: props.data.contentType,
  chapter_index: props.data.chapterIndex,
  segment_id: props.data.segmentId,
  id_chapter: props.data.id,
}));
const isMoreLinkHomeBanner = computed(() => !!(
  isMoreLinkExist.value && item.value.descriptionButtonUrl?.includes('homeBanner')
));

async function handleButtonClick() {
  $addEvent(AnalyticsEvents.ClickEvent, {
    ...analytics.value,
    item: 'details',
    action: 'shop',
  });

  if (item.value.buttonLink?.includes('concierge_form')) {
    isConciergeDialogOpen.value = true;
    return;
  }

  const link = genSPALink(item.value.buttonLink);

  if (link) {
    const external = !link.startsWith('/') || link.startsWith('//');

    await navigateTo(link, { external });
  }
}

async function handleMoreLink() {
  if (!item.value.descriptionButtonUrl) return;

  $addEvent(AnalyticsEvents.ClickEvent, {
    ...analytics.value,
    item: 'details',
  });

  // if (isMoreLinkHomeBanner.value) {
  //   moreDetailsDialogData.value = {
  //     open: true,
  //     link: item.value.descriptionButtonUrl,
  //   };

  //   return;
  // }

  const link = genSPALink(item.value.descriptionButtonUrl);

  if (link) {
    const external = !link.startsWith('/') || link.startsWith('//');

    await navigateTo(link, { external });
  }
}

async function handleMoreButton() {
  if (!item.value.descriptionButtonUrl) return;

  $addEvent(AnalyticsEvents.ClickEvent, {
    ...analytics.value,
    item: 'details',
  });

  const link = genSPALink(item.value.descriptionButtonUrl);

  if (link) {
    const external = !link.startsWith('/') || link.startsWith('//');

    await navigateTo(link, { external });
  }
}

function event() {
  $addEvent(AnalyticsEvents.SwiperBlockView, analytics.value);
}
</script>

<style lang="scss">
@import "~/assets/scss/settings/index";

.osk-home-blocks-single-banner {
  padding-top: $indent-small;
  padding-bottom: 34px;
  position: relative;

  @include media-query(md-and-up) {
    padding-bottom: 42px;
  }

  .osk-wrapper {
    padding: 0;

    @include media-query(md-and-up) {
      padding: 0 16px;
    }
  }

  &__card {
    display: flex;
    flex-direction: column;

    @include media-query(md-and-up) {
      flex-direction: row;
      border-radius: 4px;
      overflow: hidden;
    }
  }

  &__card-image {
    display: block;
    width: 100%;
    height: 300px;
    object-fit: cover;
    margin-bottom: $indent-compact;

    @include media-query(md-and-up) {
      width: 50%;
      height: 480px;
      margin-bottom: 0;
    }
  }

  &__card-content {
    padding: 0 $indent-medium;
    display: flex;
    flex-direction: column;

    @include media-query(md-and-up) {
      justify-content: center;
      align-items: center;
      text-align: center;
      padding: 0 60px;
      background: $grey-4;
      width: 50%;
    }

    @include media-query(lg-and-up) {
      padding: 0 100px;
    }

    &_noImage {
      width: 100%;
      background: none;
      padding: 0;
      margin: 0;
      display: block;
      text-align: left;

      .osk-home-blocks-single-banner__card-title {
        font-size: 18px;
        font-weight: 700;
        line-height: normal;
        text-transform: uppercase;
        margin-bottom: 15px;
        padding: 0 16px;

        @include media-query(lg-and-up) {
          padding: 0;
        }
      }

      .osk-home-blocks-single-banner__card-text {
        padding: 0 16px;
        display: block;

        @include media-query(lg-and-up) {
          padding: 0;
        }
      }

      .osk-home-blocks-single-banner__card-text_toggle {
        margin-left: 4px;
        font-weight: 600;
        text-decoration: underline;
        cursor: pointer;

        &:hover {
          text-decoration: none;
        }
      }
    }
  }

  &__card-title {
    @include line-clamp;
    @include font-style($font-size-heading, normal, $font-weight-bold);
    margin-bottom: 12px;
    margin-top: 0;
    padding: 0;

    @include media-query(md-and-up) {
      font-weight: 700;
      font-size: 32px;
      line-height: 39px;
      margin-bottom: 24px;
    }
  }

  &__card-text {
    @include line-clamp(3);
    @include font-style($font-size-body, normal, $font-weight-medium);

    @include media-query(md-and-up) {
      margin-bottom: 24px;
    }
  }

  &__card-subText {
    display: block;
    @include font-style($font-size-body, normal, $font-weight-medium);
    font-style: italic;
    color: $grey;
    margin-top: 12px;

    @include media-query(md-and-up) {
      margin-bottom: 32px;
      margin-top: 8px;
    }
  }

  &__card-more-link {
    font-weight: 600;
    text-decoration: underline;
    margin-top: 12px;

    @include media-query(lg-and-up) {
      display: none;
    }
  }

  &__card-more-btn {
    display: none;

    @include media-query(lg-and-up) {
      display: block;
      flex-shrink: 0;
      margin-right: 16px;
      min-width: 165px;
      padding: 0 16px;
    }
  }

  &__buttons {
    margin-top: 4px;

    @include media-query(lg-and-up) {
      display: flex;
      margin-top: 0;
    }
  }

  &__button {
    width: 100%;
    margin-top: 16px;

    @include media-query(lg-and-up) {
      display: flex;
      flex-shrink: 0;
      width: auto;
      min-width: 165px;
      margin-top: 0;
    }
  }

  &_isButton {
    @media screen and (max-width: 767px) {
      padding-bottom: 0;
      padding-top: 0;
      margin-bottom: 16px;

      .osk-home-blocks-single-banner__card {
        &:before {
          content: '';
          background: linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0%, #000 100%);
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
        }

        .osk-home-blocks-single-banner__card-content {
          position: absolute;
          z-index: 1;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          color: #fff;
        }

        .osk-home-blocks-single-banner__card-image {
          margin-bottom: 0;
        }

        .osk-home-blocks-single-banner__card-title {
          font-size: 32px;
          font-weight: 600;
          line-height: normal;
          text-align: center;
          margin-bottom: 16px;
          margin-top: auto;
          width: 100%;
        }

        .osk-home-blocks-single-banner__card-text {
          font-size: 13px;
          font-weight: 400;
          line-height: normal;
          text-align: center;
        }

        .osk-home-blocks-single-banner__buttons {
          margin-top: auto;
          width: 100%;
          padding-bottom: 16px;
        }

        .osk-home-blocks-single-banner__button {
          width: 100%;
        }
      }
    }
  }

  &_isJournalMode {
    padding-bottom: 0;

    @include media-query(md-and-up) {
      padding-bottom: 42px;
    }

    .osk-home-blocks-single-banner__card-content {
      padding: 0 16px;
      padding-bottom: 24px;
      margin-bottom: 24px;
      position: relative;

      @include media-query(md-and-up) {
        padding: 0 52px;
        border-bottom: 0;
        margin-bottom: 0;
      }

      &:after {
        content: '';
        width: calc(100% - 32px);
        height: 1px;
        background: #000;
        position: absolute;
        bottom: 0;
        left: 16px;

        @include media-query(md-and-up) {
          display: none;
        }
      }
    }

    .osk-home-blocks-single-banner__buttons {
      display: none;

      @include media-query(md-and-up) {
        display: flex;
      }
    }

    .osk-home-blocks-single-banner__card-text {
      @include line-clamp(4);
    }
  }

  & + .osk-home-blocks-collection:not(.osk-home-blocks-collection_noSlider) {
    margin-top: 32px;
  }

  & + .osk-home-blocks-collection_noSlider {
    @include media-query(lg-and-up) {
      margin-top: -18px;
    }
  }
}
</style>
